import { IsBoolean, IsOptional, IsString, IsUUID, ValidateIf, IsArray } from 'class-validator';
import { Type } from 'class-transformer';
import { DispositionProcessContactDto } from './disposition-process-contact.dto';

export class CreateDispositionProcessConfigRequestDto {
    /**
     * Product ID reference
     * @example '123e4567-e89b-12d3-a456-426614174000'
     */
    @ValidateIf((o) => o.productId !== '' && o.productId != null)
    @IsUUID()
    productId?: string;

    /**
     * QA Doc Package Provider Contact ID
     * @example '123e4567-e89b-12d3-a456-426614174001'
     */
    @ValidateIf((o) => o.qaDocPackageProviderContactId !== '' && o.qaDocPackageProviderContactId != null)
    @IsUUID()
    qaDocPackageProviderContactId?: string;

    /**
     * Receiving Docs Provider Contact ID
     * @example '123e4567-e89b-12d3-a456-426614174002'
     */
    @ValidateIf((o) => o.receivingDocsProviderContactId !== '' && o.receivingDocsProviderContactId != null)
    @IsUUID()
    receivingDocsProviderContactId?: string;

    /**
     * Temp Data Logger Contact ID
     * @example '123e4567-e89b-12d3-a456-426614174003'
     */
    @ValidateIf((o) => o.tempDataLoggerContactId !== '' && o.tempDataLoggerContactId != null)
    @IsUUID()
    tempDataLoggerContactId?: string;

    /**
     * Requires client action plan
     * @example false
     */
    @IsOptional()
    @IsBoolean()
    requiresClientActionPlan?: boolean;

    /**
     * Requires QCO disposition
     * @example true
     */
    @IsOptional()
    @IsBoolean()
    requiresQcoDisposition?: boolean;

    /**
     * Requires client disposition
     * @example false
     */
    @IsOptional()
    @IsBoolean()
    requiresClientDisposition?: boolean;

    /**
     * Is sampling required?
     * @example true
     */
    @IsOptional()
    @IsBoolean()
    samplingRequired?: boolean;

    /**
     * Notes related to sampling
     * @example 'Sampling notes'
     */
    @IsOptional()
    @IsString()
    samplingNotes?: string;

    /**
     * Is packaging inspection performed by Q&C?
     * @example true
     */
    @IsOptional()
    @IsBoolean()
    packagingInspectionByQnc?: boolean;

    /**
     * Comments related to packaging inspection performed by Q&C
     * @example 'Performed by Lynden as specified in Jazz Client Manual'
     */
    @IsOptional()
    @IsString()
    packagingInspectionByQncComments?: string;

    /**
     * Does test sample require datalogger?
     * @example false
     */
    @IsOptional()
    @IsBoolean()
    testSampleRequiresDatalogger?: boolean;

    /**
     * Comments related to test sample datalogger requirements
     * @example 'Yes, for Zepzelca & Rylaze. Jazz Ireland Logistics to arrange shipping confirmatory / ID samples'
     */
    @IsOptional()
    @IsString()
    testSampleRequiresDataloggerComments?: string;

    /**
     * Email for sample requests
     * @example 'samples@example.com'
     */
    @IsOptional()
    @IsString()
    sampleRequestEmailTo?: string;

    /**
     * CC emails for sample requests
     * @example 'cc1@example.com, cc2@example.com'
     */
    @IsOptional()
    @IsString()
    sampleRequestEmailCc?: string;

    /**
     * Subject line format
     * @example 'Release Notification: {ProductName}'
     */
    @IsOptional()
    @IsString()
    releaseNotificationFormat?: string;

    /**
     * Email for approvals
     * @example 'approval@example.com'
     */
    @IsOptional()
    @IsString()
    approvalEmailTo?: string;

    /**
     * CC emails for approvals
     * @example 'cc1@example.com, cc2@example.com'
     */
    @IsOptional()
    @IsString()
    approvalEmailCc?: string;

    /**
     * Notes on disposition/assessment process
     * @example 'General assessment notes'
     */
    @IsOptional()
    @IsString()
    assessmentNotes?: string;

    /**
     * Notes
     * @example 'Follow SOP-123'
     */
    @IsOptional()
    @IsString()
    notes?: string;

    /**
     * Contacts for the disposition process
     */
    @IsOptional()
    @IsArray()
    @Type(() => DispositionProcessContactDto)
    contacts?: DispositionProcessContactDto[];

    /**
     * IDs of contacts to be deleted from the disposition process
     * @example ['123e4567-e89b-12d3-a456-426614174004', '123e4567-e89b-12d3-a456-426614174005']
     */
    @IsOptional()
    @IsArray()
    @IsUUID('4', { each: true })
    deletedContactIds?: string[];
}
